'use strict';

import PhotoSwipeLightbox from './photoswipe-lightbox.esm.min.js';
import Photoswipe from './photoswipe.esm.min.js';
// js gallery
class ZoomAction extends HTMLElement {
  constructor() {
    super();
    this.type = this.dataset.type || 'no_zoom';
    this.zoomOption = this.dataset.zoomOption || 'external';
    this.lightbox = null;
    this.drift = null;
    this.lbtInstance = null;
    this.lbtV2Instance = null;
    this.init();
  }
  init() {
    if (this.type === 'no_zoom') return;
    this._resizeTimer = null;
    this._onResize = () => {
      clearTimeout(this._resizeTimer);
      this._resizeTimer = setTimeout(() => this.responsive(), 150);
    };
    window.addEventListener('resize', this._onResize);
    this._onLoad = () => this.responsive();
    window.addEventListener('load', this._onLoad);
  }
  disconnectedCallback() {
    if (this._onResize) {
      window.removeEventListener('resize', this._onResize);
      clearTimeout(this._resizeTimer);
    }
    if (this._onLoad) window.removeEventListener('load', this._onLoad);
    if (this.lightbox) { this.lightbox.destroy(); this.lightbox = null; }
    if (this.lbtInstance) { this.lbtInstance = null; }
    if (this.lbtV2Instance) { this.lbtV2Instance = null; }
  }
  _collectPswpMedia() {
    const items = [];
    const els = this.querySelectorAll('[data-media-type][data-position]');
    // Exclude thumbnail strip duplicates
    const filtered = [...els].filter(el => !el.closest('.media-thumb-swiper') && !el.closest('.thumbnail-slide'));
    const sorted = filtered.sort((a, b) => parseInt(a.dataset.position) - parseInt(b.dataset.position));
    sorted.forEach((el) => {
      const type = el.dataset.mediaType;
      const poster = el.dataset.poster || '';
      const position = parseInt(el.dataset.position, 10);
      switch (type) {
        case 'image': {
          const href = el.getAttribute('href') || el.querySelector('img')?.src || '';
          if (!href) return;
          items.push({
            src: href,
            width: parseInt(el.dataset.pswpWidth, 10) || 1200,
            height: parseInt(el.dataset.pswpHeight, 10) || 1200,
            alt: el.querySelector('img')?.alt || '',
            type: 'image',
            position
          });
          break;
        }
        case 'video':
          items.push({ type: 'video', videoSrc: el.dataset.videoSrc || '', poster, position, width: 960, height: 540 });
          break;
        case 'external_video':
          items.push({ type: 'external_video', videoHost: el.dataset.videoHost || '', videoId: el.dataset.videoId || '', poster, position, width: 960, height: 540 });
          break;
        case 'model': {
          const modelEl = el.querySelector('product-model template');
          let modelSrc = '';
          if (modelEl) {
            const tmp = document.createElement('div');
            tmp.innerHTML = modelEl.innerHTML;
            const mv = tmp.querySelector('model-viewer');
            if (mv) modelSrc = mv.getAttribute('src') || '';
          }
          items.push({ type: 'model', modelSrc, mediaId: el.dataset.mediaId || '', poster, position, width: 800, height: 800 });
          break;
        }
      }
    });
    return items;
  }

  initLightBox() {
    if (this.lightbox !== null) return;
    const zoomActionEl = this;

    // Bind click on all media types (delegated)
    this.addEventListener('click', (e) => {
      const el = e.target.closest('[data-media-type][data-position]') || e.target.closest('a.media-gallery__image');
      if (!el) return;
      e.preventDefault();
      e.stopPropagation();

      const mediaItems = zoomActionEl._collectPswpMedia();
      if (mediaItems.length === 0) return;

      let position = 0;
      const posAttr = el.getAttribute('data-position');
      if (posAttr) {
        position = parseInt(posAttr, 10) - 1;
      } else {
        const activeSlide = el.closest('media-gallery')?.querySelector('.swiper-slide-active');
        if (activeSlide && activeSlide.getAttribute('data-position')) {
          position = parseInt(activeSlide.getAttribute('data-position'), 10) - 1;
        }
      }
      if (position < 0) position = 0;
      if (position >= mediaItems.length) position = 0;

      // Destroy previous instance if any
      if (zoomActionEl.lightbox) {
        try { zoomActionEl.lightbox.destroy(); } catch (_) {}
        zoomActionEl.lightbox = null;
      }

      zoomActionEl.lightbox = new PhotoSwipeLightbox({
        pswpModule: () => Photoswipe,
        counter: false,
        zoom: false,
        preloader: false,
        arrowPrev: false,
        arrowNext: false,
        close: false,
        loop: false,
        bgOpacity: 1,
        dataSource: mediaItems,
      });

      // Custom content rendering for video/model slides
      zoomActionEl.lightbox.on('contentLoad', (e) => {
        const { content } = e;
        const itemData = content.data;
        if (itemData.type === 'video' || itemData.type === 'external_video' || itemData.type === 'model') {
          e.preventDefault();
          content.element = document.createElement('div');
          content.element.className = 'pswp__media-content';
          content.element.style.cssText = 'display:flex;align-items:center;justify-content:center;width:100%;height:100%;';

          if (itemData.type === 'video') {
            const vid = document.createElement('video');
            vid.controls = true;
            vid.autoplay = true;
            vid.muted = true;
            vid.playsInline = true;
            vid.poster = itemData.poster || '';
            vid.src = itemData.videoSrc || '';
            vid.style.cssText = 'max-width:100%;max-height:100%;border-radius:4px;';
            vid.setAttribute('aria-label', 'Product video');
            content.element.appendChild(vid);
          } else if (itemData.type === 'external_video') {
            const iframe = document.createElement('iframe');
            iframe.setAttribute('allowfullscreen', '');
            iframe.setAttribute('allow', 'autoplay; encrypted-media');
            iframe.setAttribute('frameborder', '0');
            iframe.style.cssText = 'width:100%;max-width:960px;aspect-ratio:16/9;border:none;border-radius:4px;';
            iframe.setAttribute('aria-label', 'Product video');
            if (itemData.videoHost === 'youtube') {
              iframe.src = `https://www.youtube.com/embed/${itemData.videoId}?autoplay=1&mute=1&rel=0`;
            } else {
              iframe.src = `https://player.vimeo.com/video/${itemData.videoId}?autoplay=1&muted=1`;
            }
            content.element.appendChild(iframe);
          } else if (itemData.type === 'model') {
            if (itemData.modelSrc) {
              const mv = document.createElement('model-viewer');
              mv.setAttribute('src', itemData.modelSrc);
              mv.setAttribute('camera-controls', '');
              mv.setAttribute('auto-rotate', '');
              mv.setAttribute('ar', '');
              mv.setAttribute('ar-modes', 'webxr scene-viewer quick-look');
              mv.setAttribute('poster', itemData.poster || '');
              mv.setAttribute('aria-label', '3D product model');
              mv.style.cssText = 'width:100%;height:100%;max-width:800px;max-height:80vh;';
              content.element.appendChild(mv);
              if (typeof Shopify !== 'undefined' && Shopify.loadFeatures) {
                Shopify.loadFeatures([{ name: 'model-viewer-ui', version: '1.0' }]);
              }
            } else {
              content.element.innerHTML = `<img src="${itemData.poster || ''}" alt="3D model" style="max-width:100%;max-height:100%;object-fit:contain;">`;
            }
          }
        }
      });

      // Cleanup on slide deactivate (pause video, remove iframe)
      zoomActionEl.lightbox.on('contentDeactivate', (e) => {
        const { content } = e;
        if (!content.element) return;
        const vid = content.element.querySelector('video');
        if (vid) { vid.pause(); vid.src = ''; }
        const iframe = content.element.querySelector('iframe');
        if (iframe) iframe.src = '';
      });

      // UI: close button + bottom bar
      zoomActionEl.lightbox.on('uiRegister', () => {
        const { pswp } = zoomActionEl.lightbox;
        pswp?.ui.registerElement({
          name: 'bls--close',
          isButton: true,
          tagName: 'button',
          html: '<svg width="13" height="13" viewBox="0 0 13 13" fill="none" class="transition"><use href="#icon-close"></use></svg>',
          onClick: () => {
            pswp.close();
          },
        });
        pswp?.ui.registerElement({
          name: 'bottomBar',
          className: 'pswp__bottom-bar',
          appendTo: 'wrapper',
          onInit: (el, pswp) => {
            let next, prev, counter;
            next = document.createElement('button');
            next.setAttribute('type', 'button');
            next.className = 'pswp__button pswp__button-next';
            next.innerHTML = `<svg width="6" height="11" fill="none">
              <use href="#icon-next"></use>
            </svg>`;
            next.onclick = () => {
              pswp.next();
            };
            el.appendChild(next);

            counter = document.createElement('span');
            counter.className = 'pswp__counter';
            pswp.on('change', () => {
              counter.innerText =
                pswp.currIndex +
                1 +
                pswp.options.indexIndicatorSep +
                pswp.getNumItems();
            });
            el.appendChild(counter);

            prev = document.createElement('button');
            prev.setAttribute('type', 'button');
            prev.className = 'pswp__button pswp__button-prev';
            prev.innerHTML = `<svg width="6" height="11" fill="none">
              <use href="#icon-back"></use>
            </svg>`;
            prev.onclick = () => {
              pswp.prev();
            };
            el.appendChild(prev);
          },
        });
      });

      zoomActionEl.lightbox.init();
      zoomActionEl.lightbox.loadAndOpen(position);
    });
  }
   initDrift() {
    if (this.drift !== null) return;
    var allTriggers = this.querySelectorAll('.drift-trigger');
    var paneContainer = this.closest('.sec__featured-product').querySelector(
      '.zoom-external-area'
    );
    if (allTriggers.length != 0) {
      allTriggers.forEach( (trigger) => {
        const inlineContainer = trigger.closest('[data-pane-container]');
        const zoomOption = this.zoomOption;
        if (zoomOption === 'inner-2') {
          this.drift = new Drift(trigger, {
            inlinePane: zoomOption === 'inner-2',
            zoomFactor: 3,
            containInline: paneContainer,
            paneContainer: inlineContainer,
            hoverBoundingBox: zoomOption === 'external',
            onShow: function () {
              inlineContainer.classList.add('relative');
            },
            onHide: function () {
              inlineContainer.classList.remove('relative');
            },
          });
        } else {
          this.drift = new Drift(trigger, {
            inlinePane: zoomOption,
            zoomFactor: 5,
            containInline: !!paneContainer,
            paneContainer:
              zoomOption === 'external' ? paneContainer : inlineContainer,
            hoverBoundingBox: zoomOption === 'external',
            onShow: function () {
              inlineContainer.classList.add('relative');
            },
            onHide: function () {
              inlineContainer.classList.remove('relative');
            },
          });
        }
      });
    }
  }
  initLightBoxThumbs() {
    if (this.lbtInstance !== null) return;
    if (typeof window.LightboxThumbs === 'undefined') return;
    this.lbtInstance = new window.LightboxThumbs();
    this.lbtInstance.init(this);
  }
  initLightBoxThumbsV2() {
    if (this.lbtV2Instance !== null) return;
    if (typeof window.LightboxThumbsV2 === 'undefined') return;
    this.lbtV2Instance = new window.LightboxThumbsV2();
    this.lbtV2Instance.init(this);
  }
  responsive() {
    if (this.type === 'open_lightbox_thumbs' || this.type === 'open_lightbox_thumbs_v2') {
      this.initLightBoxThumbsV2();
      this.updateSwiper();
      return;
    }
    if (window.innerWidth >= 768) {
      if (this.type === 'open_lightbox') {
        this.initLightBox();
      } else {
        if (this.lightbox !== null) {
          this.lightbox.destroy();
          this.lightbox = null;
        }
        this.initDrift();
      }
    } else {
      if (this.type !== 'open_lightbox') {
        if (this.drift !== null) {
          this.drift.destroy();
          this.drift == null;
        }
        this.initLightBox();
      } else {
        this.initLightBox();
      }
    }
    this.updateSwiper();
  }

  updateSwiper() {
    if (typeof Swiper !== 'undefined') {
      var el = this.querySelector('.thumbnail-slide');
      if (el && el.swiper) {
        el.swiper.update();
      }
    }
  }
}
customElements.define('zoom-action', ZoomAction);
